import { useEffect, useRef, useState } from 'preact/hooks';
import { MediaImage } from '../components/MediaEl';
import { ApiError, listMedia, selectMedia, uploadMedia, type ActorMedia } from '../lib/api';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { mediaLabel, mbool } from './materialsPresentation';

export interface MaterialsHeadshotsProps {
  tenantId: string;
}

// B5b — HEADSHOTS: the current looks grid. Each look is an <img> (object-URL
// pattern), with two curation toggles: "Select" (is_selected, MULTI — the same
// flag the "This is me" set reads) and "On resume" (on_resume, SINGULAR — at
// most one look prints on the resume, so setting one clears the rest here too).
// A label field + file picker adds a headshot; the grid refreshes on success.
export function MaterialsHeadshots({ tenantId }: MaterialsHeadshotsProps) {
  const [looks, setLooks] = useState<ActorMedia[] | null>(null);
  const [looksFailure, setLooksFailure] = useState<ReadFailure | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function refresh() {
    let cancelled = false;
    void listMedia(tenantId, 'headshot')
      .then((rows) => {
        if (cancelled) return;
        setLooks(rows);
        setLooksFailure(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLooks([]);
        setLooksFailure(describeReadFailure(err));
      });
    return () => {
      cancelled = true;
    };
  }

  useEffect(refresh, [tenantId]);

  async function toggle(m: ActorMedia, flag: 'is_selected' | 'on_resume') {
    if (busy) return;
    setBusy(m.id);
    setToggleError(null);
    const next = !mbool(m.fields, flag);
    try {
      await selectMedia(tenantId, m.id, { [flag]: next });
      setLooks((prev) =>
        (prev ?? []).map((x) => {
          if (x.id === m.id) return { ...x, fields: { ...x.fields, [flag]: next } };
          // on_resume is SINGULAR — setting one true clears every other look.
          if (flag === 'on_resume' && next) return { ...x, fields: { ...x.fields, on_resume: false } };
          return x;
        })
      );
    } catch (err) {
      setToggleError(
        `That look stayed as it was — ${err instanceof ApiError ? err.message : 'the server could not be reached'}`
      );
    } finally {
      setBusy(null);
    }
  }

  async function onFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadMedia(tenantId, 'headshot', file, label.trim() ? { label: label.trim() } : {});
      setLabel('');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  return (
    <div class="materials__scroll">
      <div class="materials__grp">Current looks</div>
      {looks === null ? (
        <div class="materials__empty">One moment…</div>
      ) : looksFailure ? (
        <div class="materials__error">
          {looksFailure.headline} {looksFailure.detail}
        </div>
      ) : looks.length === 0 ? (
        <div class="materials__empty">No headshots yet.</div>
      ) : (
        <div class="pgrid">
          {looks.map((m) => {
            const selected = mbool(m.fields, 'is_selected');
            const onResume = mbool(m.fields, 'on_resume');
            const tags = [selected ? 'selected' : null, onResume ? 'on resume' : null].filter(Boolean).join(' · ');
            return (
              <div key={m.id} class="lookcard">
                <div class="lookcard__art">
                  <MediaImage tenantId={tenantId} id={m.id} alt={mediaLabel(m)} />
                </div>
                <div class="lookcard__meta">
                  <div class="lookcard__t">{mediaLabel(m)}</div>
                  <div class="lookcard__r">{tags || '—'}</div>
                  <div class="lookcard__toggles">
                    <button
                      type="button"
                      class={`toggle${selected ? ' toggle--on' : ''}`}
                      aria-pressed={selected}
                      disabled={busy === m.id}
                      onClick={() => toggle(m, 'is_selected')}
                    >
                      Select
                    </button>
                    <button
                      type="button"
                      class={`toggle${onResume ? ' toggle--on' : ''}`}
                      aria-pressed={onResume}
                      disabled={busy === m.id}
                      onClick={() => toggle(m, 'on_resume')}
                    >
                      On resume
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {toggleError ? <div class="materials__error">{toggleError}</div> : null}

      <div class="materials__grp">Archive</div>
      <div class="materials__soft">Older headshots are kept.</div>

      <div class="addmedia">
        <input
          class="addmedia__label"
          type="text"
          placeholder="Label (e.g. Commercial)"
          value={label}
          onInput={(e) => setLabel((e.currentTarget as HTMLInputElement).value)}
        />
        <button
          type="button"
          class="addmedia__btn"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? 'Adding…' : '+ Add headshot'}
        </button>
        <input
          ref={fileRef}
          class="addmedia__input"
          type="file"
          accept="image/*"
          aria-label="Add headshot file"
          onChange={onFile}
        />
      </div>
      {error ? <div class="materials__error">{error}</div> : null}
    </div>
  );
}
